import { Payload } from '@e3dc-react/shell/Hooks/AuthFetch/AuthFetch'
import { AuthFetchWithResponse } from 'src/Types/api'
import { ApiResult } from '../../api'
import { UnexpectedApiResponseError } from '../../errors'
import { StatusCodes } from '../../http-status-codes'
import { DeviceLinkModel, SystemlogModel, SystemLogStatsModel, EmcStatusModel } from '../models'
import { DeviceLinksDto, SystemlogDto, InstallationsDto, AccessRequestDto, EmcStatusDto } from './dto'
import { deviceLinksDtoToModelTransformer, systemlogToModelTransformer } from './transformer'
import installationsDtoToModelTransformer from './transformer/installations-from-dto-to-model.transfromer'
import { Installation, InstallationFormModel, InstallationStats, NewInstallationFormModel } from '../models/installation.model'
import { handleFetchError } from 'src/Libs/api/utils'
import { InstallationStatementApiConnectedDto, InstallationStatementApiDto } from 'src/Libs/installation-statement/models/statement'
import { objectApiResult } from '@e3dc-react/shell/Libs/ApiHelpers'
import { StorageStatusDto } from 'src/Libs/storage'
import fromStorageStatusDtoToIsOnline from './transformer/from-storage-status-dto-to-is-online.transformer'
import fromEmcStatusDtoToModelTransformer from './transformer/from-emc-status-dto-to-model.transformer'
import { AuthFetchError } from '@e3dc-react/shell/Types/api'
import { isFlowTheme } from 'src/Libs/utils'
import { differenceInSeconds } from 'date-fns'
import { createMeter, deleteMeter, MeterPOSTorPUTDto, updateMeter } from 'src/Libs/flow/installation/api'
import { PlannedPowerMeterCreatePayload, PlannedPowerMeterModel, PlannedPowerMeterSavePayload, PowerMeterForm } from 'src/Libs/installations/models'
import { createPowerMeterPayload, createSavePowerMeterPayload } from 'src/Libs/installations/utils/create-powermeter'

//system will be considered online if the last timestamp is within 60 seconds of the current time
const VALID_ONLINE_TIME_DIFF_IN_SECONDS = 60

const plannedPowerMeterPayloadToDto = (payload: PlannedPowerMeterCreatePayload | PlannedPowerMeterSavePayload): MeterPOSTorPUTDto =>
  payload as unknown as MeterPOSTorPUTDto

const toPlannedPowerMeterApiResult = <T>(result: ApiResult<T>): ApiResult<T, AuthFetchError | Error> =>
  new ApiResult<T, AuthFetchError | Error>(result.value, result.hasErrors, result.error as AuthFetchError | Error | undefined)

export const createPlannedPowerMeter = async (
  installationId: number,
  emcDeviceLinkId: string,
  values: PowerMeterForm,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<string | null, AuthFetchError | Error>> => {
  try {
    const result = await createMeter(installationId, emcDeviceLinkId, plannedPowerMeterPayloadToDto(createPowerMeterPayload(values)), authFetch)
    return toPlannedPowerMeterApiResult(result)
  } catch (error) {
    return handleFetchError<string | null>(error)
  }
}

export const updatePlannedPowerMeter = async (
  installationId: number,
  emcDeviceLinkId: string,
  powerMeter: PlannedPowerMeterModel,
  values: PowerMeterForm,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null, AuthFetchError | Error>> => {
  try {
    const result = await updateMeter(
      installationId,
      emcDeviceLinkId,
      powerMeter.id.toString(),
      plannedPowerMeterPayloadToDto(createSavePowerMeterPayload(values, powerMeter)),
      authFetch
    )
    return toPlannedPowerMeterApiResult(result)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

export const deletePlannedPowerMeter = async (
  installationId: number,
  emcDeviceLinkId: string,
  powerMeter: PlannedPowerMeterModel,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<null, AuthFetchError | Error>> => {
  try {
    const result = await deleteMeter(installationId, emcDeviceLinkId, powerMeter.id.toString(), authFetch)
    return toPlannedPowerMeterApiResult(result)
  } catch (error) {
    return handleFetchError<null>(error)
  }
}

export const fetchOnlineStatus = async (authFetch: AuthFetchWithResponse, emsMasterDeviceId?: string): Promise<ApiResult<boolean>> => {
  if (!emsMasterDeviceId) {
    return handleFetchError(new Error('emsMasterDeviceId missing'))
  }

  try {
    const response = await authFetch<StorageStatusDto>({
      path: `storages/${emsMasterDeviceId}/status`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      const lastTimestamp = response.data.time ? new Date(response.data.time) : undefined
      const isOnline = lastTimestamp ? differenceInSeconds(new Date(), lastTimestamp) <= VALID_ONLINE_TIME_DIFF_IN_SECONDS : false

      return new ApiResult<boolean>(isOnline)
    }

    throw new UnexpectedApiResponseError(response.status)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}

export const fetchEmcStatus = async (authFetch: AuthFetchWithResponse, emsMasterDeviceId?: string): Promise<ApiResult<EmcStatusModel>> => {
  if (!emsMasterDeviceId) {
    return handleFetchError(new Error('emsMasterDeviceId missing'))
  }

  try {
    const response = await authFetch<EmcStatusDto>({
      path: `storages/${emsMasterDeviceId}/status`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<EmcStatusModel>(fromEmcStatusDtoToModelTransformer(response.data))
    }

    throw new UnexpectedApiResponseError(response.status)
  } catch (error) {
    return handleFetchError<EmcStatusModel>(error)
  }
}

/**
 * Returns a boolean informing if an installation is online based on a given storageId.
 */
export const fetchIsInstallationOnline = async (storageId: string | undefined, authFetch: AuthFetchWithResponse): Promise<ApiResult<boolean>> => {
  if (storageId === undefined || storageId === '') {
    return new ApiResult<boolean>(null, true, new Error('no storageId provided'))
  }

  try {
    const response = await authFetch<StorageStatusDto>({
      path: `storages/${storageId}/status`,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      if (response.data.time === '') console.error('installation-online-debug: timestamp fetched is empty', response)
      return new ApiResult<boolean>(fromStorageStatusDtoToIsOnline(response.data))
    }

    if (response.status === StatusCodes.NO_CONTENT) {
      console.error('installation-online-debug: timestamp fetched returned no-content', response)
      return new ApiResult<boolean>(false)
    }

    throw new UnexpectedApiResponseError(response.status)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}

export const fetchSystemLogs = async (installationId: number, payload: Payload, authFetch: AuthFetchWithResponse): Promise<SystemlogModel[]> => {
  const response = await authFetch<SystemlogDto>({
    path: `installations/${installationId}/systemlogs`,
    method: 'GET',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload,
  })

  if (response.status === StatusCodes.OK) {
    return systemlogToModelTransformer(response.data as unknown as SystemlogDto)
  }

  throw new UnexpectedApiResponseError(response.status)
}

export const fetchSystemLogStats = async (
  installationId: number,
  payload: Payload,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<SystemLogStatsModel>> => {
  try {
    const response = await authFetch<SystemLogStatsModel>({
      path: `installations/${installationId}/systemlogs/stats`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload,
    })
    if (response.status === StatusCodes.OK) {
      return new ApiResult<SystemLogStatsModel>(response.data)
    }

    return new ApiResult<SystemLogStatsModel>(null, true)
  } catch (error) {
    return handleFetchError<SystemLogStatsModel>(error)
  }
}

export const updateSystemLog = async (installationId: number, log: { id: number }, authFetch: AuthFetchWithResponse): Promise<void> => {
  const response = await authFetch<void>({
    path: `installations/${installationId}/systemlogs`,
    method: 'PUT',
    endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    payload: log,
  })
  if (response.status !== StatusCodes.NO_CONTENT) {
    throw new UnexpectedApiResponseError(response.status)
  }
}

export const getInstallationsByDeviceId = async (deviceId: number, authFetch: AuthFetchWithResponse): Promise<ApiResult<Installation[]>> => {
  try {
    const response = await authFetch<InstallationsDto>({
      path: 'installations',
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload: { deviceLinkFilter: `deviceId=${deviceId}`, ...(isFlowTheme && { filter: 'appName=ISNULL' }) },
    })
    if (response.status === StatusCodes.OK) {
      const data = installationsDtoToModelTransformer(response.data)
      return new ApiResult<Installation[]>(data)
    }
    return new ApiResult<Installation[]>(null, true)
  } catch (error) {
    return handleFetchError<Installation[]>(error)
  }
}

export const getInstallations = async (authFetch: AuthFetchWithResponse): Promise<ApiResult<Installation[]>> => {
  try {
    const response = await authFetch<InstallationsDto>({
      path: 'installations',
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      ...(isFlowTheme && { payload: { filter: 'appName=ISNULL' } }),
    })
    if (response.status === StatusCodes.OK) {
      const data = installationsDtoToModelTransformer(response.data)
      return new ApiResult<Installation[]>(data)
    }
    return new ApiResult<Installation[]>(null, true)
  } catch (error) {
    return handleFetchError<Installation[]>(error)
  }
}

export const fetchInstallationsStats = async (authFetch: AuthFetchWithResponse): Promise<ApiResult<number>> => {
  try {
    const response = await authFetch<InstallationStats>({
      path: 'installations/stats',
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<number>(response.data.count)
    }
    return new ApiResult<number>(0, true)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}

export const fetchInstallationsNoAccessStats = async (authFetch: AuthFetchWithResponse, accId: number): Promise<ApiResult<number>> => {
  if (isFlowTheme) {
    return new ApiResult<number>(0, true)
  }
  try {
    const response = await authFetch<InstallationStats>({
      path: `installations/account-links/revoked-access/${accId}/stats`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<number>(response.data.count)
    }
    return new ApiResult<number>(0, true)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}

export const getDeviceLinkByDeviceId = async (
  installationId: number,
  deviceId: number,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<DeviceLinkModel[]>> => {
  try {
    const response = await authFetch<DeviceLinksDto>({
      path: `installations/${installationId}/device-links`,
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload: { deviceLinkFilter: `deviceId=${deviceId}` },
    })
    if (response.status === StatusCodes.OK) {
      const data = deviceLinksDtoToModelTransformer(response.data)
      return new ApiResult<DeviceLinkModel[]>(data)
    }
    return new ApiResult<DeviceLinkModel[]>(null, true)
  } catch (error) {
    return handleFetchError<DeviceLinkModel[]>(error)
  }
}

export const fetchInstallation = async (
  installationId: number | undefined,
  authFetch: AuthFetchWithResponse,
  log?: (value: string) => void
): Promise<ApiResult<Installation>> => {
  try {
    const response = await authFetch<Installation>({
      path: `installations/${installationId}`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<Installation>(response.data)
    }

    log?.(`fetchInstallation. installationId: ${installationId}, response: ${JSON.stringify(response)}`)

    return new ApiResult<Installation>(null, true)
  } catch (error) {
    log?.(`fetchInstallation. installationId: ${installationId}, error: ${JSON.stringify(error)}`)

    return handleFetchError<Installation>(error)
  }
}

export const updateInstallation = async (
  installationId: number,
  installationData: Partial<InstallationFormModel>,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationFormModel>> => {
  try {
    const response = await authFetch<InstallationFormModel>({
      path: `installations/${installationId}`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'PUT',
      payload: installationData,
    })

    if ([StatusCodes.OK, StatusCodes.NO_CONTENT].includes(response.status)) {
      return new ApiResult<InstallationFormModel>(response.data)
    }

    return new ApiResult<InstallationFormModel>(null, true)
  } catch (error) {
    return handleFetchError<InstallationFormModel>(error)
  }
}

export const fetchInstallationStatementsByDeviceLink = async (
  installationId: number | undefined,
  deviceLinkId: number | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationStatementApiDto>> => {
  if (!installationId) return new ApiResult<InstallationStatementApiDto>(null, true, new Error('no installationId provided'))
  if (!deviceLinkId) return new ApiResult<InstallationStatementApiDto>(null, true, new Error('no deviceLinkId provided'))

  try {
    const response = await authFetch<InstallationStatementApiDto>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      return new ApiResult<InstallationStatementApiDto>(response.data)
    }

    if (response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<InstallationStatementApiDto>(null)
    }

    return new ApiResult<InstallationStatementApiDto>(null, true)
  } catch (error) {
    return handleFetchError<InstallationStatementApiDto>(error)
  }
}

export const fetchCurrentInstallationStatementByDeviceLink = async (
  installationId: number | undefined,
  deviceLinkId: number | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationStatementApiDto>> => {
  if (!installationId) return new ApiResult<InstallationStatementApiDto>(null, true, new Error('no installationId provided'))
  if (!deviceLinkId) return new ApiResult<InstallationStatementApiDto>(null, true, new Error('no deviceLinkId provided'))

  try {
    const response = await authFetch<InstallationStatementApiDto>({
      path: `installations/${installationId}/device-links/${deviceLinkId}/statements/current`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK || response.status === StatusCodes.NO_CONTENT) {
      return new ApiResult<InstallationStatementApiDto>(response.data)
    }

    return new ApiResult<InstallationStatementApiDto>(null, true)
  } catch (error) {
    return handleFetchError<InstallationStatementApiDto>(error)
  }
}

export const fetchInstallationStatements = async (
  installationId: number | undefined,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<InstallationStatementApiDto[]>> => {
  if (!installationId) return new ApiResult<InstallationStatementApiDto[]>(null, true, new Error('no installationId provided'))

  try {
    const response = await authFetch<InstallationStatementApiConnectedDto[]>({
      path: `installations/${installationId}/statements`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      method: 'GET',
    })

    if (response.status === StatusCodes.OK) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return new ApiResult<InstallationStatementApiDto[]>(objectApiResult<any, InstallationStatementApiDto[]>(response.data))
    }

    return new ApiResult<InstallationStatementApiDto[]>(null, true)
  } catch (error) {
    return handleFetchError<InstallationStatementApiDto[]>(error)
  }
}

/**
 * send request for access to installation
 */
export const sendAccessRequest = async (
  installationId: number,
  serialNumber: string,
  accountLinkId: number,
  reason: string,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<boolean, AuthFetchError | Error>> => {
  try {
    const managementLink = `${process.env.REACT_APP_BASEURL}/dashboard/overview/${installationId}/${serialNumber}`
    const response = await authFetch<AccessRequestDto>({
      path: `installations/${installationId}/account-links/${accountLinkId}/access-request`,
      method: 'POST',
      payload: { reason, managementLink },
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })
    if (response.status === StatusCodes.CREATED) {
      return new ApiResult<boolean, Error | AuthFetchError>(true)
    }
    return new ApiResult<boolean, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<boolean>(error)
  }
}

export const getInstallationsAttachedToCompany = async (companyId: string, authFetch: AuthFetchWithResponse): Promise<ApiResult<Installation[]>> => {
  try {
    const response = await authFetch<InstallationsDto>({
      path: `installations`,
      payload: { accountLinkFilter: `accountId=${companyId},accountType=company` },
      method: 'GET',
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
    })

    if (response.status === StatusCodes.OK) {
      const data = installationsDtoToModelTransformer(response.data)

      return new ApiResult<Installation[]>(data)
    }
    return new ApiResult<Installation[]>(null, true)
  } catch (error) {
    return handleFetchError<Installation[]>(error)
  }
}

export const fetchOwnedInstallations = async (
  authFetch: AuthFetchWithResponse,
  userId?: number
): Promise<ApiResult<Installation[], Error | AuthFetchError>> => {
  try {
    if (!userId) return new ApiResult<Installation[], Error | AuthFetchError>(null, true)
    const response = await authFetch<InstallationsDto>({
      path: `installations`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload: { accountLinkFilter: `accountId=${userId}`, filter: `ownerAccountId=${userId}${isFlowTheme ? ',appName=ISNULL' : ''}` },
      method: 'GET',
    })
    if (response.status === StatusCodes.OK) {
      const data = installationsDtoToModelTransformer(response.data)
      return new ApiResult<Installation[], Error | AuthFetchError>(data)
    }

    return new ApiResult<Installation[], Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<Installation[]>(error)
  }
}

export const createNewInstallation = async (
  payload: NewInstallationFormModel,
  authFetch: AuthFetchWithResponse
): Promise<ApiResult<number, Error | AuthFetchError>> => {
  try {
    const { storageType, ...restPayload } = payload
    const response = await authFetch<number>({
      path: `installations`,
      endpoint: process.env.REACT_APP_INSTALLATIONS_ENDPOINT,
      payload: { ...restPayload, installationStatus: 'planned' },
      method: 'POST',
    })
    if (response.status === StatusCodes.CREATED) {
      return new ApiResult<number, Error | AuthFetchError>(response.data)
    }

    return new ApiResult<number, Error | AuthFetchError>(null, true)
  } catch (error) {
    return handleFetchError<number>(error)
  }
}
